// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// Enhanced Status Badge Component - Improved visual indicators for campaigns
// Date: July 26, 2025

import React from 'react';
import { AdminCampaign } from '../../utils/csrAssignment';

interface EnhancedStatusBadgeProps {
  campaign: AdminCampaign;
  showPriority?: boolean;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Get priority level based on campaign status and age
 */
const getCampaignPriority = (campaign: AdminCampaign): 'low' | 'medium' | 'high' | 'urgent' => {
  const createdDate = new Date(campaign.created_at);
  const now = new Date();
  const daysOld = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
  
  switch (campaign.status.toLowerCase()) {
    case 'submitted':
      // Submitted campaigns need attention quickly
      return daysOld > 2 ? 'urgent' : daysOld > 1 ? 'high' : 'medium';
    case 'preparing':
      // Preparing campaigns should move to running quickly
      return daysOld > 3 ? 'urgent' : daysOld > 1 ? 'high' : 'medium';
    case 'running':
      // Running campaigns need monitoring
      return daysOld > 14 ? 'high' : 'low';
    case 'draft':
      // Draft campaigns can wait
      return daysOld > 7 ? 'medium' : 'low';
    case 'completed':
    case 'cancelled':
      return 'low';
    default:
      return 'medium';
  }
};

/**
 * Get enhanced status styles with priority indicators
 */
const getEnhancedStatusStyles = (status: string, priority: string): string => {
  const baseStatus = status.toLowerCase();
  
  // Base status colors
  let baseClasses = '';
  switch (baseStatus) {
    case 'draft':
      baseClasses = 'bg-gray-100 text-gray-800 border-gray-200';
      break;
    case 'submitted':
      baseClasses = 'bg-blue-100 text-blue-800 border-blue-200';
      break;
    case 'preparing':
      baseClasses = 'bg-yellow-100 text-yellow-800 border-yellow-200';
      break;
    case 'running':
      baseClasses = 'bg-green-100 text-green-800 border-green-200';
      break;
    case 'completed':
      baseClasses = 'bg-purple-100 text-purple-800 border-purple-200';
      break;
    case 'paused':
      baseClasses = 'bg-orange-100 text-orange-800 border-orange-200';
      break;
    case 'cancelled':
      baseClasses = 'bg-red-100 text-red-800 border-red-200';
      break;
    default:
      baseClasses = 'bg-gray-100 text-gray-800 border-gray-200';
  }
  
  // Add priority-based border thickness
  switch (priority) {
    case 'urgent':
      baseClasses += ' border-2 shadow-md';
      break;
    case 'high':
      baseClasses += ' border-2';
      break;
    case 'medium':
      baseClasses += ' border';
      break;
    case 'low':
    default:
      baseClasses += ' border';
      break;
  }
  
  return baseClasses;
};

/**
 * Get priority indicator emoji
 */
const getPriorityIndicator = (priority: string): string => {
  switch (priority) {
    case 'urgent':
      return '🔴';
    case 'high':
      return '🟡';
    case 'medium':
      return '🟢';
    case 'low':
    default:
      return '';
  }
};

/**
 * Get size classes for the badge
 */
const getSizeClasses = (size: 'small' | 'medium' | 'large'): string => {
  switch (size) {
    case 'small':
      return 'px-2 py-0.5 text-xs';
    case 'large':
      return 'px-3 py-1 text-sm';
    case 'medium':
    default:
      return 'px-2.5 py-0.5 text-xs';
  }
};

/**
 * Format status text with proper capitalization
 */
const formatStatusText = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

/**
 * Get campaign age in human-readable format
 */
const getCampaignAge = (createdAt: string): string => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks === 1) return '1w ago';
  if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
  
  return createdDate.toLocaleDateString();
};

const EnhancedStatusBadge: React.FC<EnhancedStatusBadgeProps> = ({
  campaign,
  showPriority = false,
  size = 'medium'
}) => {
  const priority = getCampaignPriority(campaign);
  const statusStyles = getEnhancedStatusStyles(campaign.status, priority);
  const sizeClasses = getSizeClasses(size);
  const priorityIndicator = showPriority ? getPriorityIndicator(priority) : '';
  const campaignAge = getCampaignAge(campaign.created_at);
  
  return (
    <div className="flex flex-col items-start space-y-1">
      <span 
        className={`inline-flex items-center rounded-full font-medium ${statusStyles} ${sizeClasses}`}
        title={`Status: ${formatStatusText(campaign.status)}${showPriority ? ` • Priority: ${priority}` : ''} • Created: ${campaignAge}`}
      >
        {priorityIndicator && (
          <span className="mr-1" aria-label={`${priority} priority`}>
            {priorityIndicator}
          </span>
        )}
        {formatStatusText(campaign.status)}
      </span>
      
      {/* Additional contextual information for certain statuses */}
      {(campaign.status.toLowerCase() === 'submitted' || campaign.status.toLowerCase() === 'preparing') && (
        <div className="text-xs text-gray-500">
          {campaignAge}
        </div>
      )}
      
      {/* Priority warning for urgent items */}
      {priority === 'urgent' && (
        <div className="text-xs text-red-600 font-medium">
          Needs attention
        </div>
      )}
    </div>
  );
};

export default EnhancedStatusBadge;